<template>
  <button
    class="IconButton"
    type="button"
    :data-variant="variant"
    :data-shape="shape"
    :data-size="size"
  >
    <Transition>
      <Icon
        :key="iconName"
        class="IconButton__icon"
        :name="iconName"
        :size="iconSize"
      />
    </Transition>
  </button>
</template>

<script setup lang="ts">
import { IconName, IconSize } from "./Icon.types";
import Icon from "./Icon.vue";

const {
  variant = "primary",
  size = "medium",
} = defineProps<{
  variant?: "primary" | "secondary",
  iconName: keyof typeof IconName,
  iconSize?: IconSize,
  shape?: "circle",
  size?: "small" | "smedium" | "medium",
}>();
</script>

<style lang="scss" scoped>
.IconButton {
  --clickAreaSize: 44px;
  --buttonSize: 44px;
  --insetSize: calc((var(--clickAreaSize) - var(--buttonSize)) / 2);
  --blockOffset: calc(-1 * (var(--clickAreaSize) - 24px) / 2);
  position: relative;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: var(--clickAreaSize);
  height: var(--clickAreaSize);
  margin: var(--blockOffset) calc(-1 * var(--insetSize));

  color: var(--iconColor, inherit);

  transition: color 350ms;

  &[data-size="small"] {
    --buttonSize: 24px;
  }

  &[data-size="smedium"] {
    --buttonSize: 28px;
  }

  &[data-shape] {
    --borderColor: var(--themeColorNeutral900);
  }

  &[data-shape][data-variant="primary"] {
    --iconColor: var(--themeColorSecondaryForeground);
    --backgroundColor: var(--themeColorSecondary);
  }

  &[data-shape][data-variant="secondary"] {
    --iconColor: var(--themeColorNeutral900);
    --backgroundColor: var(--themeColorNeutral100);
  }

  &:disabled[data-shape] {
    --iconColor: var(--themeColorNeutral500);
    --backgroundColor: var(--themeColorNeutral150);
    --borderColor: var(--themeColorNeutral400);
  }

  &[data-shape]::before {
    background-color: var(--backgroundColor);
  }

  &[data-shape="circle"]::before {
    position: absolute;
    inset: var(--insetSize);
    z-index: -1;

    border: solid 2px var(--borderColor);
    border-radius: 50%;

    transition: 350ms;
    transition-property: background-color, border-color;

    content: "";
  }
}

.IconButton__icon {
  &.v-enter-active {
    transition: 350ms var(--easeOutBack);
    transition-property: opacity, transform;
  }

  &.v-leave-active {
    position: absolute;
    transition: 175ms var(--easeInQuad);
    transition-property: opacity, transform;
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }

  :deep(path) {
    transition: 250ms fill;
  }
}
</style>
